import { isOnSale } from '~/js/utils/util-functions';

/**
 * Lift the Clerk chat launcher clear of the bar.
 *
 * The launcher lives in a shadow root, so an inline style is the only way to reach it from
 * here. translateY rather than `bottom`/`margin`, so the launcher and the expanded chat
 * panel shift as one and neither ends up under the bar.
 *
 * Re-applied on every show/hide rather than set up once, so a widget that mounts after the
 * first reveal is picked up on the next one. A no-op while Clerk is absent.
 */
let chatLauncherMissWarned = false;

// Which mounted bar currently owns the shared page state (body class + chat launcher
// transform). Module-level because the Theme Editor can have a stale instance and a live
// one alive at once, and only the owner may reset what it set.
let activeOwner = null;

const offsetChatLauncher = (offset) => {
  const launcher = document
    .querySelector('clerk-sales-assistant')
    ?.shadowRoot?.querySelector('.chat-container');

  if (!launcher) {
    // Only worth saying when we actually wanted it moved, and once only, since this fires on
    // every reveal. info rather than warn: the widget is injected externally, so a blocked
    // or absent one is a normal session — and a warning that fires on normal sessions is how
    // a real selector change ends up ignored.
    if (offset && !chatLauncherMissWarned) {
      chatLauncherMissWarned = true;
      console.info(
        '[sticky-atc] clerk-sales-assistant .chat-container not found; the bar may cover the chat launcher. Expected if Clerk is blocked or not loaded on this session; otherwise check the vendor selector.'
      );
    }
    return;
  }

  // Transform only on reset — the transition is left in place so the launcher glides back
  // down alongside the bar instead of snapping. `clearChatLauncherStyles` drops it once
  // the instance is going away entirely.
  if (!offset) {
    launcher.style.transform = '';
    return;
  }

  launcher.style.transition = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches
    ? 'none'
    : 'transform 0.3s ease';
  launcher.style.transform = `translateY(-${offset}px)`;
};

// Leave the vendor's element exactly as we found it when an instance goes away.
const clearChatLauncherStyles = () => {
  const launcher = document
    .querySelector('clerk-sales-assistant')
    ?.shadowRoot?.querySelector('.chat-container');
  if (!launcher) return;

  launcher.style.transform = '';
  launcher.style.transition = '';
};

/**
 * Sticky add-to-cart bar (AB Lyft experiment: pdp-sticky-atc).
 *
 * Presentation and a click proxy — deliberately no cart logic and no variant state of its
 * own, so the buy box stays the single source of truth:
 *
 * - button state is mirrored from the buy box's own button with a MutationObserver, which
 *   covers every CTA state plus the transient "Adding…" text through one mechanism rather
 *   than re-deriving the rules that decide them;
 * - clicks are forwarded to that button, so everything the real add-to-cart path does
 *   happens once, where it already lives;
 * - price mounts as a nested [data-component="price"] and is not handled here.
 *
 * Rendering a second buy box instead was rejected: it would hold its own variant state and
 * stay pinned to whichever variant the server rendered.
 */
export default (node) => {
  // initProductPage() re-runs on shopify:section:load, which would otherwise re-observe
  // and double-bind in the Theme Editor.
  if (node.dataset.stickyAtcMounted) return;

  const buyBox = document.querySelector('.product-ctas');
  if (!buyBox) {
    console.warn('[sticky-atc] .product-ctas not found; bar not mounted.');
    return;
  }

  // Precedence for picking the button the buy box is currently showing. *At most* one of
  // these lacks `hidden` — zero is a legitimate state, not an impossible one: an
  // out-of-stock variant with a blank out_of_stock_strategy and no MTO tag hides all
  // three. syncButton handles that case explicitly, so do not delete the noPrimaryVisible
  // machinery on the assumption that one is always visible.
  //
  // Re-queried on every sync rather than captured. Note this does not protect against a
  // wholesale buy box replacement — the query is rooted at the `buyBox` captured at
  // mount, so a replaced root would still resolve detached buttons. The real guard for
  // that is the `isConnected` assert in handleClick.
  const findPrimaryButtons = () =>
    [
      buyBox.querySelector('[data-button-add-to-cart]'),
      buyBox.querySelector('[data-button-notify-me]'),
      buyBox.querySelector('[data-button-sold-out]'),
    ].filter(Boolean);

  const primaryButtons = findPrimaryButtons();
  const stickyButton = node.querySelector('[data-sticky-atc-button]');
  const variantLabel = node.querySelector('[data-sticky-atc-variant]');
  const compareLabel = node.querySelector('[data-sticky-atc-compare]');
  const stockLabel = node.querySelector('[data-sticky-atc-stock]');

  // The buy box renders no buttons at all for BookEasy products — a legitimate no-op, and
  // the only one. Everything else here is markup this snippet always renders, so a miss
  // means drift that would silently ship the variant arm an empty bar while AB Lyft keeps
  // bucketing sessions into it. Distinguish the two in the log.
  if (!primaryButtons.length) {
    console.warn(
      '[sticky-atc] no buy box CTA found; expected only for BookEasy products, otherwise check add-to-cart.liquid.'
    );
    return;
  }

  if (!stickyButton || !variantLabel || !compareLabel || !stockLabel) {
    console.warn(
      '[sticky-atc] bar markup incomplete; check sticky-add-to-cart.liquid.'
    );
    return;
  }

  // The bar reads one value off this attribute, so an absent or renamed vocabulary would
  // silently mean "never in select state" — the bar would then mirror the buy box's
  // *disabled* "Select size" button and hand the shopper a dead CTA, which is strictly
  // worse than control. Bail rather than warn-and-continue, like the guards above: no bar
  // at all leaves the shopper with the control experience, which is the safe failure.
  const CTA_STATES = ['add', 'mto', 'select', 'notify', 'sold-out', 'none'];
  if (!CTA_STATES.includes(buyBox.dataset.ctaState)) {
    console.warn(
      `[sticky-atc] unrecognised data-cta-state "${buyBox.dataset.ctaState}"; expected one of ${CTA_STATES.join(', ')}. Bar not mounted — check add-to-cart.liquid still publishes it.`
    );
    return;
  }

  node.dataset.stickyAtcMounted = 'true';

  const productId = node.dataset.productId;
  const productTitle = node.dataset.productTitle || '';
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  let activePrimary = primaryButtons[0];
  let needsVariantChoice = false;
  let noPrimaryVisible = false;
  let scrolledPast = false;
  let isActive = null;
  // Warned once per instance: reservedHeaderSpace() runs on every resize event, and the
  // memo in syncTriggerObserver short-circuits only after it has already been called.
  let headspaceMissWarned = false;

  const syncButton = () => {
    const visible = findPrimaryButtons().find(
      (button) => !button.classList.contains('hidden')
    );

    // The buy box can legitimately show no CTA at all: an out-of-stock variant whose
    // out_of_stock_strategy metafield is blank and which carries no MTO tag hides all
    // three buttons. Mirror that by hiding the bar, rather than leaving a stale label
    // wired to a button the shopper is not meant to be able to press.
    const hadPrimary = !noPrimaryVisible;
    noPrimaryVisible = !visible;
    const hasPrimary = !noPrimaryVisible;

    if (visible) {
      activePrimary = visible;

      // The buy box disables its add-to-cart button and relabels it "Select size" until a
      // variant is picked. The bar keeps that CTA clickable instead and sends the shopper
      // back up to the dropdown.
      //
      // Read from the published state, not inferred by comparing the button's text to its
      // select label: that comparison breaks the moment a label gains a prefix or a
      // translation is edited on one side only, and it fails closed — handing the variant
      // arm a dead disabled CTA, which is worse than showing no bar at all.
      needsVariantChoice = buyBox.dataset.ctaState === 'select';

      const label = visible.textContent.trim();
      stickyButton.textContent = label;
      stickyButton.disabled = needsVariantChoice ? false : visible.disabled;

      // Built from the mirrored text rather than copied off the source button, whose own
      // accessible name is not refreshed when its text swaps — copying it leaves the bar
      // announcing one label while displaying another.
      stickyButton.setAttribute(
        'aria-label',
        productTitle ? `${label} - ${productTitle}` : label
      );
    }

    // Only when the CTA's presence actually flipped. syncButton runs on every buy box
    // mutation and setVisible reads getClientRects(), forcing layout. Visibility depends
    // solely on scrolledPast and noPrimaryVisible, so neither a text change nor a disabled
    // toggle can alter it — re-evaluating on those was pure thrash on the hot path.
    if (hasPrimary !== hadPrimary) applyVisibility();
  };

  /**
   * Mirror the buy box's low-stock count instead of re-deriving it.
   *
   * Mirroring the rendered node is what stops the bar disagreeing with the buy box: every
   * rule for whether a count shows at all stays in one place, in the Liquid.
   *
   * The buy box renders one of two mutually exclusive branches — the delivery estimate or
   * the plain count — so take whichever currently has a box. `data-inventory-count` marks
   * the count itself on one branch and the whole wrapper on the other, so resolve to the
   * leaf: the wrapper's text carries more than the count, and the client-rect test needs
   * the count's own visibility.
   */
  const stockCountNodes = () =>
    Array.from(
      document.querySelectorAll(
        '[data-component="variantInfo"] [data-inventory-count]'
      )
    ).map((el) => el.querySelector('[data-variant-inventory-count]') || el);

  const syncStock = () => {
    const source = stockCountNodes().find(
      (el) => el.getClientRects().length > 0
    );
    const text = source ? source.textContent.trim() : '';

    stockLabel.textContent = text;
    stockLabel.classList.toggle('hidden', !text);
  };

  const handleVariantChange = (event) => {
    const { variant, productId: eventProductId } = event.detail;
    if (eventProductId !== productId) return;
    if (!variant) return;

    variantLabel.textContent = variant.title || '';

    // Compare-at price is the bar's own to update — nothing else reaches this node. Prices
    // in the variant payload arrive pre-formatted.
    if (isOnSale(variant)) {
      compareLabel.textContent = variant.compare_at_price;
      compareLabel.classList.remove('hidden');
    } else {
      compareLabel.classList.add('hidden');
    }

    // Stock is not handled here: syncStock mirrors the buy box's own node, and the observer
    // below covers the case where it has not been refreshed yet.
  };

  const setVisible = (visible) => {
    node.classList.toggle('translate-y-full', !visible);
    node.classList.toggle('invisible', !visible);

    // Control-arm guard. AB Lyft only injects the reveal CSS for sessions bucketed into
    // the variant, so for everyone else the bar stays display:none and has no client
    // rects — without this check we would shift a control shopper's chat bubble to make
    // room for a bar that is not there.
    const isRendered = node.getClientRects().length > 0;
    const active = visible && isRendered;

    // Memoised on the *resolved* state, not on `visible`. AB Lyft can inject the reveal
    // CSS after the first scroll-past (which is why it re-applies on experimentViewed as
    // well as DOMContentLoaded); memoising on `visible` would latch the bar shown with
    // `active` false and never apply the body padding or the chat offset again.
    if (active === isActive) return;
    isActive = active;

    // Measured rather than hardcoded so the chat offset and the body padding cannot
    // drift from the real bar height.
    if (active) {
      document.documentElement.style.setProperty(
        '--sticky-atc-height',
        `${node.offsetHeight}px`
      );
    }

    // Claim or release ownership of the shared page state, so a stale instance cannot
    // later reset what this one set.
    if (active) activeOwner = node;
    else if (activeOwner === node) activeOwner = null;

    // Enforced here rather than only in teardown: the shared writes below are the thing
    // ownership protects, so the invariant belongs next to them instead of depending on
    // applyVisibility staying the sole caller that screens on node.isConnected.
    if (activeOwner !== null && activeOwner !== node) return;

    offsetChatLauncher(active ? node.offsetHeight : 0);
    document.body.classList.toggle('sticky-atc-visible', active);
  };

  // Two independent conditions gate the bar — the shopper has scrolled past the buy box,
  // and the buy box has a CTA worth mirroring. Keeping them as state rather than deriving
  // one from the other means switching back to a purchasable variant reveals the bar
  // immediately, without waiting for the next scroll.
  const applyVisibility = () => {
    // The section is replaced wholesale in the Theme Editor. Both entry points into this
    // function are document-level (experimentViewed) or bound to the old buy box, so a
    // stale instance could otherwise strip the live instance's body class and chat offset.
    if (!node.isConnected) {
      teardown();
      return;
    }

    setVisible(scrolledPast && !noPrimaryVisible);
  };

  const handleResize = () => {
    // Matches applyVisibility and handleIntersect: a stale Theme-Editor instance would
    // otherwise keep this listener, warn about a bar that no longer exists, and write
    // 0px measurements over the live one.
    if (!node.isConnected) {
      teardown();
      return;
    }

    // Ahead of the isActive gate: `.headspace` switches at tab_l, and the shopper is
    // usually still above the buy box when they cross it. Gating this would leave the
    // observer inset at the other breakpoint's value until a resize happened to occur
    // while the bar was visible. Idempotent and self-memoising, so it is cheap to re-run.
    syncTriggerObserver();

    // Gated on this instance's own state, not the shared body class — a stale instance
    // would otherwise pass the guard and write 0px from its detached node's zero height.
    if (!isActive) return;

    document.documentElement.style.setProperty(
      '--sticky-atc-height',
      `${node.offsetHeight}px`
    );
    offsetChatLauncher(node.offsetHeight);
  };

  // Re-queried at click time rather than captured at mount: this CTA is deliberately
  // enabled while the buy box's is disabled, so a stale reference would turn it into a
  // silent dead end. Falls back to the buy box itself, which is never the wrong place to
  // send someone who pressed "Select size".
  const scrollToVariantSelect = () => {
    const variantSelect = buyBox.querySelector('[data-variant-select]');
    const target = variantSelect || buyBox;

    if (!variantSelect) {
      console.warn(
        '[sticky-atc] [data-variant-select] not found; scrolling to the buy box instead.'
      );
    }

    // block: 'center' puts the target mid-viewport, which clears the fixed header
    // without needing to know its height.
    target.scrollIntoView({
      behavior: prefersReducedMotion.matches ? 'auto' : 'smooth',
      block: 'center',
    });
    // preventScroll so focusing does not fight the smooth scroll still in flight.
    if (variantSelect) variantSelect.focus({ preventScroll: true });
  };

  const handleClick = () => {
    if (noPrimaryVisible) return;

    // Bar-engagement goal: fires for both the "Select size" press and a real add, which is
    // what the name says. Add-to-cart conversion itself is measured by the global goal. The
    // tracker is only defined on shops running ABLyft, so guard on it — a missing tracker
    // means the readout silently under-counts.
    if (typeof window.ablyftTrack === 'function') {
      window.ablyftTrack('pdp-sticky-atc-click');
    } else {
      console.warn(
        '[sticky-atc] ablyftTrack unavailable; click goal not recorded.'
      );
    }

    if (needsVariantChoice) {
      scrollToVariantSelect();
      return;
    }

    // The buy box is not re-rendered on the PDP today, but a detached button would
    // swallow the add entirely — cheap to assert, impossible to debug otherwise.
    if (!activePrimary.isConnected) {
      console.error('[sticky-atc] buy box CTA is detached; add not forwarded.');
      return;
    }

    activePrimary.click();
  };

  const buttonObserver = new MutationObserver(syncButton);

  // Watches the buy box's own count rather than reacting to variant:change, so the bar does
  // not depend on listener ordering for that event.
  const stockObserver = new MutationObserver(syncStock);

  const handleIntersect = ([entry]) => {
    if (!node.isConnected) {
      teardown();
      return;
    }

    // Measured against the *inset* root, not the layout viewport: the CTA counts as gone the
    // moment it passes under the header. Comparing against 0 instead would make the inset
    // matter only while the buy box is taller than it, so a shorter buy box would silently
    // bring the late reveal back.
    // The height check keeps an all-zero rect — what the observer reports for a target with
    // no box — from reading as "scrolled past" and revealing the bar at the top of the page.
    const insetTop = entry.rootBounds?.top ?? observedHeaderSpace ?? 0;
    const rect = entry.boundingClientRect;
    scrolledPast = rect.height > 0 && rect.bottom <= insetTop;
    applyVisibility();
  };

  /**
   * How much room the fixed header takes, used to inset the observer's root so "scrolled
   * past" means past the *visible* viewport rather than the layout one. Without it the CTA
   * keeps intersecting long after it has slid under the header.
   *
   * Read from `.headspace`'s padding rather than by measuring the header element, because
   * that padding is the theme's own reservation for the whole header stack. It is the
   * fully-expanded value, so a collapsed header reveals the bar slightly early — the safe
   * direction, since early beats the CTA being gone with no replacement.
   */
  const reservedHeaderSpace = () => {
    const headspace = node.closest('.headspace');
    const reserved = headspace
      ? parseInt(getComputedStyle(headspace).paddingTop, 10)
      : NaN;

    // `!reserved` rather than `Number.isNaN`: the realistic drift is the `.headspace`
    // padding rule being removed or renamed, which yields '0px' → 0, not NaN. A 0px
    // header inset is never legitimate for a node inside `.headspace`, and falling back
    // to it silently restores exactly the bug this inset exists to fix — the memo in
    // syncTriggerObserver would then latch it for the rest of the pageview.
    if (!reserved) {
      if (!headspaceMissWarned) {
        headspaceMissWarned = true;
        console.warn(
          '[sticky-atc] could not read .headspace padding; reveal threshold falls back to the layout viewport, so the bar will appear late by roughly the header height.'
        );
      }
      return 0;
    }

    return reserved;
  };

  let triggerObserver = null;
  let observedHeaderSpace = null;

  const syncTriggerObserver = () => {
    const reserved = reservedHeaderSpace();
    if (reserved === observedHeaderSpace) return;

    observedHeaderSpace = reserved;
    if (triggerObserver) triggerObserver.disconnect();

    triggerObserver = new IntersectionObserver(handleIntersect, {
      threshold: 0,
      rootMargin: `-${reserved}px 0px 0px 0px`,
    });
    triggerObserver.observe(buyBox);
  };

  function teardown() {
    // Only this instance's own listeners and observers are unconditionally dropped. The
    // body class and the chat launcher are *shared* state, so they are reset only by the
    // instance that currently owns them — ownership, not this instance's own history. A
    // stale instance that was visible when the section reloaded would otherwise strip
    // them from a freshly-mounted bar, which memoises on isActive and would never
    // re-assert them.
    const ownsSharedState = activeOwner === node;

    isActive = null;
    if (triggerObserver) triggerObserver.disconnect();
    buttonObserver.disconnect();
    stockObserver.disconnect();
    document.removeEventListener('variant:change', handleVariantChange);
    document.removeEventListener('experimentViewed', applyVisibility);
    window.removeEventListener('resize', handleResize);

    if (!ownsSharedState) return;
    activeOwner = null;
    document.body.classList.remove('sticky-atc-visible');
    clearChatLauncherStyles();
  }

  buttonObserver.observe(buyBox, {
    subtree: true,
    childList: true,
    characterData: true,
    // data-cta-state lives on the observed node itself, not a descendant, so the sync
    // re-runs when the buy box republishes its state.
    attributeFilter: ['class', 'disabled', 'aria-label', 'data-cta-state'],
  });
  // Observe the whole buy box info column rather than the count nodes themselves. Those
  // are the wrong unit: there are none to observe at mount on a sold-out/MTO/restock
  // render, and ABLyft reveals an arm by changing an *ancestor*, which produces no
  // mutation record on a count node. The column is always present and covers both.
  const variantInfo = document.querySelector('[data-component="variantInfo"]');

  if (variantInfo) {
    stockObserver.observe(variantInfo, {
      subtree: true,
      childList: true,
      characterData: true,
      attributeFilter: ['class'],
    });
  } else {
    // Legitimately absent renders no count; a missing column is drift. Distinguish them,
    // or a renamed hook silently ships a bar whose stock line never appears.
    console.warn(
      '[sticky-atc] [data-component="variantInfo"] not found; the bar will never show a stock count.'
    );
  }

  syncTriggerObserver();
  stickyButton.addEventListener('click', handleClick);
  document.addEventListener('variant:change', handleVariantChange);
  window.addEventListener('resize', handleResize);

  // AB Lyft fires this when it buckets the session, which can land after the shopper has
  // already scrolled past the buy box. Without it a late reveal would leave the bar shown
  // but never picked up as active, since nothing else re-evaluates until the next scroll.
  document.addEventListener('experimentViewed', applyVisibility);

  syncButton();
  syncStock();
};
